import { Injector } from '@angular/core';

import { AbstractFieldsModel } from './abstractFieldsModel';
import { QuestionFormService } from '@modules/management/pages/details/check/services/question/question-form.service';
import { IUserFilter, matchesUserFilter } from '@services/accounts/user-filter';
import {
  AccountsService,
  CertificationsService,
  FormField,
  PermissionsService,
  RolesService,
  TeamsService,
  UserdataService,
  UserService
} from '@services';

import { TranslateService } from '@ngx-translate/core';
import { filter, get } from 'lodash';

// userFilter key -> the form field that edits it
export const USER_FILTER_FIELDS: { [key in keyof IUserFilter]-?: string } = {
  teams: 'userFilterTeams',
  locations: 'userFilterLocations',
  certifications: 'userFilterCertifications',
  roles: 'userFilterRoles',
  permissions: 'userFilterPermissions'
};

export const USER_QUESTION_TYPES: string[] = ['singleUser', 'multiUser'];

export class UserFieldsModel extends AbstractFieldsModel {
  private translate: TranslateService = this.injector.get(TranslateService);
  private accountsService: AccountsService = this.injector.get(AccountsService);
  private teamsService: TeamsService = this.injector.get(TeamsService);
  private userService: UserService = this.injector.get(UserService);
  private userdataService: UserdataService = this.injector.get(UserdataService);
  private certificationsService: CertificationsService = this.injector.get(CertificationsService);
  private rolesService: RolesService = this.injector.get(RolesService);
  private permissionsService: PermissionsService = this.injector.get(PermissionsService);

  constructor(
    protected questionFormService: QuestionFormService,
    protected injector: Injector
  ) {
    super(questionFormService);
  }

  public getFields(): FormField[] {
    const locations: number[] = this.userdataService.locations || [];
    const onFilterChange = () => this.updateMatchCount();

    return [
      {
        containerClass: 'question-type-field user',
        title: 'MGMT_DETAILS.Question_Input_Details',
        type: 'subtitle'
      },
      {
        containerClass: 'question-field question-type-field user',
        name: 'userSelectionType',
        title: this.translate.instant('MGMT_DETAILS.Selection_Type'),
        type: 'selectmenu',
        multiple: false,
        searchable: false,
        canClear: false,
        originalOrder: true,
        default: 'singleUser',
        options: [
          {id: 'singleUser', description: 'MGMT_DETAILS.Single_User_Option'},
          {id: 'multiUser', description: 'MGMT_DETAILS.Multi_User_Option'}
        ]
      },
      {
        containerClass: 'question-type-field user',
        title: 'MGMT_DETAILS.User_Filter',
        type: 'subtitle'
      },
      {
        containerClass: 'question-field question-type-field user',
        title: 'SHARED.Team(s)',
        name: USER_FILTER_FIELDS.teams,
        type: 'selectmenu',
        placeholder: this.translate.instant('SHARED.Any_Team'),
        multiple: true,
        searchable: true,
        canClear: true,
        valueProperty: 'groupID',
        originalOrder: true,
        options: this.teamsService.getTeamList(locations, true),
        test: (team: any) => !get(team, 'disabledAt'),
        func: (team: any) => this.teamsService.teamName(team),
        onChange: onFilterChange
      },
      {
        containerClass: 'question-field question-type-field user',
        title: 'SHARED.Locationss',
        name: USER_FILTER_FIELDS.locations,
        type: 'selectmenu',
        placeholder: this.translate.instant('SHARED.All_Locations'),
        multiple: true,
        searchable: true,
        canClear: true,
        valueProperty: 'locationID',
        options: this.userService.getUserLocations(locations, false),
        func: (location: any) => location.name,
        onChange: onFilterChange
      },
      {
        containerClass: 'question-field question-type-field user',
        title: 'SHARED.Certification(s)',
        name: USER_FILTER_FIELDS.certifications,
        type: 'selectmenu',
        placeholder: this.translate.instant('SHARED.Any_Certification'),
        multiple: true,
        searchable: true,
        canClear: true,
        valueProperty: 'certificationID',
        options: this.certificationsService.certifications.data || [],
        test: (certification: any) => !get(certification, 'disabledAt'),
        func: (certification: any) => this.certificationsService.certificationName(certification, false),
        onChange: onFilterChange
      },
      {
        containerClass: 'question-field question-type-field user',
        title: 'MGMT_DETAILS.Roles',
        name: USER_FILTER_FIELDS.roles,
        type: 'selectmenu',
        placeholder: this.translate.instant('MGMT_DETAILS.Any_Role'),
        multiple: true,
        searchable: true,
        canClear: true,
        valueProperty: 'roleID',
        options: this.rolesService.roles,
        test: (role: any) => !get(role, 'disabledAt'),
        func: (role: any) => this.rolesService.roleName(role),
        onChange: onFilterChange
      },
      {
        containerClass: 'question-field question-type-field user',
        title: 'SHARED.EDIT_Permissions',
        name: USER_FILTER_FIELDS.permissions,
        type: 'selectmenu',
        placeholder: this.translate.instant('SHARED.Any_Permissions'),
        multiple: true,
        searchable: true,
        canClear: true,
        valueProperty: 'id',
        options: this.permissionsService.permissions.data,
        // the same visibility rule the deployment form applies to these two
        test: (permission: any) => permission.id === 'corvex'
          ? !!get(this.userdataService.Permissions, 'corvex')
          : permission.id !== 'sadmin' || !!(get(this.userdataService.Permissions, 'sadmin') || get(this.userdataService.Permissions, 'corvex')),
        func: (permission: any) => this.translate.instant(permission.description),
        onChange: onFilterChange
      },
      {
        // filled in by updateMatchCount so an admin sees when a filter matches nobody
        containerClass: 'question-field question-type-field user user-filter-match',
        title: '',
        type: 'subtitle'
      }
    ];
  }

  /**
   * Shows how many active users the filter currently offers. The worker's list is resolved
   * live when the check is answered, so this is a preview, not a stored value.
   */
  public updateMatchCount(): void {
    const element: HTMLElement = this.questionFormService.getElementBy('.user-filter-match');

    if (element) {
      const prefix: string = this.questionFormService.questionPage.formConfig.prefix;
      const userFilter: IUserFilter = this.questionFormService.getUserFilter((name: string) => $(`#${prefix}${name}`).val());
      const count: number = filter(this.accountsService.accounts.data, (account) => matchesUserFilter(account, userFilter)).length;

      element.textContent = this.translate.instant('MGMT_DETAILS.User_Filter_Match_Count', {count});
    }
  }
}
